import { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  Shield, Flame, Gauge, Cpu, Bot, GitPullRequest, ScrollText, Lock, GitBranch,
  ShieldCheck, Award, UsersRound, FileWarning, Plug, Users, Settings,
  ChevronLeft, ChevronRight, ChevronDown, Clock, Link2
} from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

const navGroups = [
  {
    label: 'Security',
    items: [
      { name: 'AI Security Dashboard', icon: Shield, path: '/dashboard' },
      { name: 'Incident Review', icon: Flame, path: '/dashboard/incident-review', badge: '1', badgeColor: '#EF4444' },
      { name: 'Risk Score', icon: Gauge, path: '/dashboard/risk-score' },
    ],
  },
  {
    label: 'AI Control',
    items: [
      { name: 'AI Inventory', icon: Cpu, path: '/dashboard/ai-inventory' },
      { name: 'Agent Controls', icon: Bot, path: '/dashboard/agent-controls' },
      { name: 'Change Control', icon: GitPullRequest, path: '/dashboard/change-control' },
      { name: 'Decision Ledger', icon: ScrollText, path: '/dashboard/decision-ledger', badge: '47.8K' },
    ],
  },
  {
    label: 'Governance Proof',
    items: [
      { name: 'Evidence Vault', icon: Lock, path: '/dashboard/evidence-vault' },
      { name: 'Proof Chain', icon: Link2, path: '/dashboard/proof-chain', sublabel: 'Chain Integrity Engine' },
      { name: 'Lineage', icon: GitBranch, path: '/dashboard/lineage' },
      { name: 'Proof Status', icon: ShieldCheck, path: '/dashboard/compliance' },
      { name: 'Assurance Center', icon: Award, path: '/dashboard/assurance' },
      { name: 'Roles', icon: UsersRound, path: '/dashboard/roles' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { name: 'Exceptions', icon: FileWarning, path: '/dashboard/exceptions', badge: '4' },
    ],
  },
  {
    label: 'Settings',
    items: [
      { name: 'Integrations', icon: Plug, path: '/dashboard/integrations' },
      { name: 'Team', icon: Users, path: '/dashboard/team' },
      { name: 'Settings', icon: Settings, path: '/dashboard/settings' },
    ],
  },
];

const roadmapItems = [
  { name: 'Apex SOC', timeline: 'Q4 2026' },
  { name: 'Apex Cloud', timeline: 'Q1 2027' },
];

export default function Sidebar({ collapsed, onToggle, mobileOpen, onMobileClose }) {
  const location = useLocation();
  const [roadmapOpen, setRoadmapOpen] = useState(false);

  return (
    <aside
      data-testid="sidebar"
      className={`
        ${collapsed ? 'lg:w-[72px]' : 'lg:w-64'}
        fixed lg:relative z-50 lg:z-auto
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        w-64 transition-all duration-300 bg-[#080D1A] border-r border-[#1E2A45] flex flex-col h-screen flex-shrink-0
      `}
    >
      {/* Logo */}
      <div className="h-16 flex items-center gap-3 px-4 border-b border-[#1E2A45]">
        <div className="w-9 h-9 rounded-lg bg-[#6366F1]/20 flex items-center justify-center flex-shrink-0">
          <Shield size={20} className="text-[#6366F1]" strokeWidth={2} />
        </div>
        {(!collapsed || mobileOpen) && (
          <div className="overflow-hidden">
            <div className="text-[#F8FAFC] font-bold text-lg tracking-tight leading-none">APEX</div>
            <div className="text-[#64748B] text-[10px] tracking-widest uppercase">Temporal AI Governance</div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 py-3">
        {navGroups.map((group) => (
          <div key={group.label} className="mb-2">
            {(!collapsed || mobileOpen) && (
              <div className="px-4 py-2 text-[10px] font-semibold text-[#475569] uppercase tracking-[0.12em]">
                {group.label}
              </div>
            )}
            {group.items.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`nav-link flex items-center gap-3 mx-2 px-3 py-2 rounded-lg text-sm transition-all ${
                    isActive
                      ? 'active bg-[#6366F1]/10 text-[#6366F1]'
                      : 'text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#0F1629]'
                  } ${collapsed ? 'lg:justify-center' : ''}`}
                  onClick={() => onMobileClose && onMobileClose()}
                >
                  <Icon size={18} strokeWidth={1.5} className="flex-shrink-0" />
                  {(!collapsed || mobileOpen) && (
                    <>
                      <div className="flex-1 truncate">
                        <span>{item.name}</span>
                        {item.sublabel && <div className="text-[8px] text-[#6366F1] opacity-70 truncate">{item.sublabel}</div>}
                      </div>
                      {item.badge && (
                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${item.badgeColor || '#6366F1'}15`, color: item.badgeColor || '#6366F1' }}>
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                </Link>
              );
            })}
          </div>
        ))}

        {/* Roadmap */}
        {(!collapsed || mobileOpen) && (
          <div className="mb-2">
            <button
              onClick={() => setRoadmapOpen(!roadmapOpen)}
              className="w-full flex items-center gap-3 mx-2 px-3 py-2 text-[10px] font-semibold text-[#475569] uppercase tracking-[0.12em]"
              data-testid="roadmap-toggle"
            >
              <Clock size={12} />
              <span>Roadmap</span>
              <ChevronDown size={12} className={`ml-auto transition-transform ${roadmapOpen ? 'rotate-180' : ''}`} />
            </button>
            {roadmapOpen && (
              <div className="mx-2 space-y-1">
                {roadmapItems.map((item) => (
                  <div key={item.name} className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-[#475569] cursor-default">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#475569]" />
                    <span className="flex-1 text-[#475569]">{item.name}</span>
                    <span className="text-[9px] text-[#475569]">{item.timeline}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </ScrollArea>

      {/* Collapse toggle */}
      <button
        data-testid="sidebar-toggle"
        onClick={onToggle}
        className="hidden lg:flex h-12 items-center justify-center border-t border-[#1E2A45] text-[#64748B] hover:text-[#F8FAFC] transition-colors"
      >
        {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
      </button>
    </aside>
  );
}
